import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useMemo, useState } from "react";
import { useGroupMemberIdsQuery } from "../../App/Queries/GroupQueries";
import { useCandidateSelection } from "../../App/State/Hooks/useCandidateSelection";
import { ISelectedCandidate } from "../../App/State/Slices/MessageDialogSlice";
import { useGroupCandidatesFilterStore } from "../../App/State/Stores/GroupCandidatesPaginationStore";
import { ICandidate } from "../../App/Typings/Candidate";

interface IGroupCandidateSelectionContext {
  getters: {
    actionCandidate: ICandidate | undefined;
    selectedCandidateIdList: string[];
    selectedCandidates: ISelectedCandidate[];
    selectedCount: number;
    selectAll: boolean;
    excludedCandidateIds: string[];
    disableSelectAll: boolean;
    userSelected: boolean;
    isCandidateSelected: (candidateId: string) => boolean;
    currentPageSelectedIds: string[];
  };
  setters: {
    setActionCandidate: Dispatch<SetStateAction<ICandidate | undefined>>;
    setCandidateSelectedList: (ids: string[]) => void;
    setTotalAvailableCount: (count: number) => void;
  };
  handlers: {
    handleSelectedAll: (selected: boolean) => void;
    handleSelectRow: (row: ICandidate, select: boolean) => void;
  };
}

const defaultGroupCandidateSelection = {
  getters: {
    actionCandidate: undefined,
    selectedCandidateIdList: [],
    selectedCandidates: [],
    selectedCount: 0,
    selectAll: false,
    excludedCandidateIds: [],
    disableSelectAll: false,
    userSelected: false,
    isCandidateSelected: (_: string) => false,
    currentPageSelectedIds: [],
  },
  setters: {
    setActionCandidate: () => {},
    setCandidateSelectedList: () => {},
    setTotalAvailableCount: () => {},
  },
  handlers: {
    handleSelectedAll: () => {},
    handleSelectRow: () => {},
  },
};

export const GroupCandidateSelectionContext = createContext<IGroupCandidateSelectionContext>(
  defaultGroupCandidateSelection,
);

export function GroupCandidateSelectionProvider({
  groupId,
  children,
}: {
  groupId: string;
  children: ReactNode;
}) {
  const [actionCandidate, setActionCandidate] = useState<ICandidate | undefined>();
  const [totalAvailableCount, setTotalAvailableCount] = useState<number>(0);
  const [userSelected, setUserSelected] = useState<boolean>(false);
  const { idsFilterQueryString } = useGroupCandidatesFilterStore(groupId);
  const { data: memberIds = [] } = useGroupMemberIdsQuery(groupId, idsFilterQueryString, true);

  const {
    getters: {
      selectedCandidateIds,
      selectedCount,
      selectAll,
      excludedCandidateIds,
      disableSelectAll,
      isCandidateSelected,
      currentPageSelectedIds,
      getSelectionCountAfter,
    },
    handlers: { handleSelectRow: handleSelectCandidate, handleSelectAllClick },
    setters: { setSelectedCandidateIds },
  } = useCandidateSelection({
    totalAvailableCount,
  });

  const selectedCandidates = useMemo(() => {
    if (selectAll) {
      return memberIds
        .filter((id) => !excludedCandidateIds.includes(id))
        .map((id) => ({ candidateId: id }) as ISelectedCandidate);
    }
    return selectedCandidateIds.map((id) => ({ candidateId: id }) as ISelectedCandidate);
  }, [selectAll, selectedCandidateIds, memberIds, excludedCandidateIds]);

  const handleSelectedAll = (selected: boolean) => {
    handleSelectAllClick(selected);
    setUserSelected(selected);
  };

  const handleSelectRow = (row: ICandidate, select: boolean) => {
    handleSelectCandidate(row, select);
    if (select && !userSelected) {
      setUserSelected(true);
    } else if (!select && getSelectionCountAfter(row.id, select) === 0) {
      setUserSelected(false);
    }
  };

  const setCandidateSelectedList = (ids: string[]) => {
    setSelectedCandidateIds(ids);
  };

  return (
    <GroupCandidateSelectionContext.Provider
      value={{
        getters: {
          actionCandidate,
          selectedCandidateIdList: selectedCandidateIds,
          selectedCandidates,
          selectedCount,
          selectAll,
          excludedCandidateIds,
          disableSelectAll,
          userSelected,
          isCandidateSelected,
          currentPageSelectedIds,
        },
        setters: {
          setActionCandidate,
          setCandidateSelectedList,
          setTotalAvailableCount,
        },
        handlers: {
          handleSelectedAll,
          handleSelectRow,
        },
      }}
    >
      {children}
    </GroupCandidateSelectionContext.Provider>
  );
}

export const useGroupCandidateSelectionContext = () => useContext(GroupCandidateSelectionContext);
